/**
 * MorningMeetingActivityLog.jsx
 * 
 * Activity log viewer for morning meeting reports
 * Shows all changes, who made them, and when
 */

import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import './Morningmeetingactivitylog.css';

const API = 'http://localhost:3001/MorningMeeting';
const getToken = () => window.localStorage.getItem('acessToken');

export default function MorningMeetingActivityLog() {
  const token = getToken();

  const [logs, setLogs] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadData();
  }, [selectedDate, filterAction, limit]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load reports list
      const reportsRes = await Axios.get(`${API}/list`, {
        params: { token, limit: 100 }
      });

      if (reportsRes.data.success) {
        setReports(reportsRes.data.data);
      }

      // Load activity logs
      const params = { token, limit };
      if (selectedDate) {
        params.meeting_date = selectedDate;
      }

      const logsRes = await Axios.get(`${API}/activity-log`, { params });

      if (logsRes.data.success) {
        let filteredLogs = logsRes.data.data;

        // Filter by action type if selected
        if (filterAction) {
          filteredLogs = filteredLogs.filter(log => log.action_type === filterAction);
        }

        setLogs(filteredLogs);
      }
    } catch (error) {
      console.error('Error loading activity log:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'CREATE': return '✨';
      case 'UPDATE': return '✏️';
      case 'PUBLISH': return '✓';
      case 'DELETE': return '🗑️';
      default: return '•';
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'CREATE': return '#6bcf7f';
      case 'UPDATE': return '#ffa502';
      case 'PUBLISH': return '#4a90e2';
      case 'DELETE': return '#ff4757';
      default: return '#6b6b6b';
    }
  };

  const renderChangeDetails = (log) => {
    if (log.action_type === 'PUBLISH' || log.action_type === 'CREATE') {
      return <div className="mmal__change-text">Report {log.action_type.toLowerCase()}d</div>;
    }

    try {
      const oldVal = log.old_value;
      const newVal = log.new_value;

      if (!oldVal && !newVal) {
        return <div className="mmal__change-text">No change details available</div>;
      }

      // For complex objects, show a summary
      if (typeof newVal === 'object' && newVal !== null) {
        const keys = Object.keys(newVal);
        return (
          <div className="mmal__change-details">
            <div className="mmal__change-label">Modified sections:</div>
            <div className="mmal__change-list">
              {keys.map((key, idx) => (
                <span key={idx} className="mmal__change-tag">{key}</span>
              ))}
            </div>
          </div>
        );
      }

      return (
        <div className="mmal__change-comparison">
          {oldVal && (
            <div className="mmal__change-old">
              <span className="mmal__change-label">From:</span>
              <span className="mmal__change-value">{JSON.stringify(oldVal)}</span>
            </div>
          )}
          {newVal && (
            <div className="mmal__change-new">
              <span className="mmal__change-label">To:</span>
              <span className="mmal__change-value">{JSON.stringify(newVal)}</span>
            </div>
          )}
        </div>
      );
    } catch (error) {
      return <div className="mmal__change-text">Change details unavailable</div>;
    }
  };

  if (loading) {
    return (
      <div className="mmal">
        <div className="mmal__loading">
          <div className="mmal__spinner" />
          <div>Loading activity log...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mmal">
      {/* Header */}
      <div className="mmal__header">
        <div className="mmal__header-content">
          <h1 className="mmal__title">Activity Log</h1>
          <p className="mmal__subtitle">Morning Meeting Reports Audit Trail</p>
        </div>
        <button
          className="mmal__btn mmal__btn--back"
          onClick={() => window.location.href = '/morning-meeting'}
        >
          ← Back to Reports
        </button>
      </div>

      <div className="mmal__container">
        {/* Filters */}
        <div className="mmal__filters">
          <div className="mmal__filter-group">
            <label className="mmal__filter-label">Meeting Date</label>
            <select
              className="mmal__filter-select"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            >
              <option value="">All Dates</option>
              {reports.map((report, idx) => (
                <option key={idx} value={report.meeting_date}>
                  {new Date(report.meeting_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })} - {report.status}
                </option>
              ))}
            </select>
          </div>

          <div className="mmal__filter-group">
            <label className="mmal__filter-label">Action Type</label>
            <select
              className="mmal__filter-select"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="PUBLISH">Publish</option>
              <option value="DELETE">Delete</option>
            </select>
          </div>

          <div className="mmal__filter-group">
            <label className="mmal__filter-label">Limit</label>
            <select
              className="mmal__filter-select"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
            >
              <option value="25">25 entries</option>
              <option value="50">50 entries</option>
              <option value="100">100 entries</option>
              <option value="250">250 entries</option>
            </select>
          </div>

          <button
            className="mmal__filter-reset"
            onClick={() => {
              setSelectedDate('');
              setFilterAction('');
              setLimit(50);
            }}
          >
            Reset Filters
          </button>
        </div>

        {/* Stats */}
        <div className="mmal__stats">
          <div className="mmal__stat">
            <div className="mmal__stat-value">{logs.length}</div>
            <div className="mmal__stat-label">Total Entries</div>
          </div>
          <div className="mmal__stat">
            <div className="mmal__stat-value">{reports.length}</div>
            <div className="mmal__stat-label">Total Reports</div>
          </div>
          <div className="mmal__stat">
            <div className="mmal__stat-value">
              {logs.filter(l => l.action_type === 'UPDATE').length}
            </div>
            <div className="mmal__stat-label">Updates</div>
          </div>
          <div className="mmal__stat">
            <div className="mmal__stat-value">
              {logs.filter(l => l.action_type === 'PUBLISH').length}
            </div>
            <div className="mmal__stat-label">Published</div>
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="mmal__timeline">
          {logs.length === 0 ? (
            <div className="mmal__empty">
              <div className="mmal__empty-icon">📋</div>
              <div className="mmal__empty-text">No activity logs found</div>
              <div className="mmal__empty-subtext">
                Try adjusting your filters or check back after making some changes
              </div>
            </div>
          ) : (
            logs.map((log, idx) => (
              <div key={idx} className="mmal__log-item">
                <div className="mmal__log-indicator">
                  <div
                    className="mmal__log-dot"
                    style={{ background: getActionColor(log.action_type) }}
                  >
                    {getActionIcon(log.action_type)}
                  </div>
                  {idx < logs.length - 1 && <div className="mmal__log-line" />}
                </div>

                <div className="mmal__log-card">
                  <div className="mmal__log-header">
                    <div className="mmal__log-meta">
                      <span
                        className="mmal__log-action"
                        style={{ color: getActionColor(log.action_type) }}
                      >
                        {log.action_type}
                      </span>
                      <span className="mmal__log-separator">•</span>
                      <span className="mmal__log-section">{log.section_changed}</span>
                    </div>
                    <div className="mmal__log-time">{formatTimestamp(log.changed_at)}</div>
                  </div>

                  <div className="mmal__log-info">
                    <div className="mmal__log-user">
                      <span className="mmal__log-label">By:</span>
                      <span className="mmal__log-value">{log.changed_by || 'System'}</span>
                    </div>
                    <div className="mmal__log-date">
                      <span className="mmal__log-label">Meeting Date:</span>
                      <span className="mmal__log-value">
                        {new Date(log.meeting_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  {(log.old_value || log.new_value) && (
                    <div className="mmal__log-changes">
                      {renderChangeDetails(log)}
                    </div>
                  )}

                  {log.ip_address && (
                    <div className="mmal__log-footer">
                      <span className="mmal__log-ip">IP: {log.ip_address}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}